import React, { FC, useMemo } from 'react';
import cx from 'classnames';
import {
  Typography,
  TableBody,
  Paper,
  TableRow,
  Table,
  TableContainer,
  Grid,
  TablePagination,
} from '@material-ui/core';
import TableCell from '@material-ui/core/TableCell';
import { makeStyles } from '@material-ui/styles';
import { loading } from 'src/assets/images';
import { AnyObject, InfoTableProps } from 'src/commons/types';
import _ from 'lodash';
import { EnhancedTableHead } from './components/EnhancedTableHead';
import { grey } from '../../../theme/colors';

const useStyles = makeStyles({
  root: {
    width: '100%',
    marginTop: '15px',
  },
  tableContainer: {
    maxHeight: '60vh',
    overflowY: 'auto',
    overflowX: 'auto',
    scrollbarGutter: 'stable',
  },
  search: {
    display: 'flex',
    justifyContent: 'center',
  },
  caption: {
    height: '15px',
  },
  typography: {
    fontSize: 13,
    color: grey.dark,
    textOverflow: 'true',
  },
  arrayContainer: {
    backgroundColor: 'transparent',
    boxShadow: 'none',
  },
  seccionNames: {
    '&:hover': {
      fontWeight: 600,
    },
  },
  countContainer: {
    paddingTop: 15,
    paddingBottom: 15,
    textAlign: 'center',
    boxSizing: 'border-box',
  },
  stickyCell: {
    position: 'sticky',
    left: 0,
    zIndex: 1,
    backgroundColor: '#fff',
    boxShadow: '2px 0 4px rgba(0,0,0,0.06)',
  },
  pagination: {
    borderTop: '1px solid rgba(224, 224, 224, 1)',
  },
});

const getCellLayoutStyle = (column: AnyObject) => ({
  minWidth: column.minWidth,
  width: column.width,
});

const InfoTableRaw: FC<InfoTableProps> = ({
  columns,
  rows,
  onBottomScroll,
  onRowClick,
  rowIdKey = 'id',
  onSortChange,
  orderBy,
  direction: order = 'asc',
  working,
  customStyle,
  customClassnameTypography,
  showCount,
  headerPadding,
  pagination,
  tableMinWidth,
}: InfoTableProps) => {
  const classes = useStyles();

  const resolvedTableMinWidth = useMemo(() => {
    if (tableMinWidth) return tableMinWidth;
    const sum = columns.reduce((total, column) => total + (column.minWidth ?? 0), 0);
    return sum > 0 ? sum : undefined;
  }, [columns, tableMinWidth]);

  const handleScroll = (e: any) => {
    const bottom = e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + 100;
    if (bottom && onBottomScroll) {
      onBottomScroll();
    }
  };

  const handleRequestSort = (event: AnyObject, property: any) => {
    if (onSortChange) {
      onSortChange(property, order === 'asc' ? 'desc' : 'asc');
    }
  };

  const labelDisplayedRows = ({ from, to, count }: { from: number; to: number; count: number }) =>
    `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`;

  return (
    <>
      <Paper className={classes.root}>
        <TableContainer
          className={`${classes.tableContainer} ${customStyle}`}
          onScroll={handleScroll}
        >
          <Table
            stickyHeader
            aria-label="sticky table"
            style={resolvedTableMinWidth ? { minWidth: resolvedTableMinWidth } : undefined}
          >
            <EnhancedTableHead
              headCells={columns}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              headerPadding={headerPadding}
            />
            <TableBody>
              {working ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    align="center"
                    style={{ padding: '10px', maxWidth: '200px' }}
                  >
                    <Grid
                      container
                      justify="center"
                      alignItems="center"
                      style={{ width: '100%', height: '50px' }}
                    >
                      <img src={loading} width={50} alt="loading" />
                    </Grid>
                  </TableCell>
                </TableRow>
              ) : !rows || rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center">
                    <Typography variant="body1" color="textSecondary">
                      No hay datos disponibles
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row: any) => (
                  <TableRow
                    hover
                    role="checkbox"
                    tabIndex={-1}
                    key={row[rowIdKey]}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    style={{ ...row.customStyles }}
                  >
                    {columns
                      .filter((column) => {
                        const value = row[column.id];
                        if (column.hide === undefined) {
                          return true;
                        }
                        return typeof column.hide === 'boolean'
                          ? !column.hide
                          : !column.hide(value, row);
                      })
                      .map((column: any) => {
                        const value = _.get(row, column.id);
                        return (
                          <TableCell
                            key={column.id}
                            align={column.align}
                            className={column.sticky ? classes.stickyCell : undefined}
                            style={{
                              ...getCellLayoutStyle(column),
                              ...column.customStyles,
                            }}
                          >
                            <Typography
                              component="span"
                              className={cx(customClassnameTypography, classes.typography)}
                            >
                              {column.render ? column.render(value, row) : value}
                            </Typography>
                          </TableCell>
                        );
                      })}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {pagination ? (
          <TablePagination
            component="div"
            className={classes.pagination}
            count={pagination.count}
            page={pagination.page}
            rowsPerPage={pagination.rowsPerPage}
            rowsPerPageOptions={[pagination.rowsPerPage]}
            onChangePage={(_event, newPage) => pagination.onPageChange(newPage)}
            labelDisplayedRows={labelDisplayedRows}
            labelRowsPerPage="Registros por página:"
          />
        ) : (
          !!(showCount && rows?.length) && (
            <div className={classes.countContainer}>
              Registros cargados:
              {rows?.length}
            </div>
          )
        )}
      </Paper>
    </>
  );
};

export const InfoTable = React.memo(InfoTableRaw);
